import React, { useState, useEffect } from 'react';
import {
  getAdminUsers,
  grantAdminAccess,
  revokeAdminAccess
} from '../../services/adminApiService';

const AdminUserManagement = ({ adminUser }) => {
  const [adminUsers, setAdminUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    onshapeUserId: '',
    userEmail: '',
    userDisplayName: '',
    role: 'admin',
    notes: ''
  });

  useEffect(() => {
    loadAdminUsers();
  }, [includeInactive]);

  async function loadAdminUsers() {
    try {
      setLoading(true);
      setError(null);
      const response = await getAdminUsers(includeInactive);
      setAdminUsers(response.adminUsers || []);
    } catch (err) {
      console.error('Error loading admin users:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function handleInputChange(e) {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  }

  function handleCancelForm() {
    setShowForm(false);
    setFormData({
      onshapeUserId: '',
      userEmail: '',
      userDisplayName: '',
      role: 'admin',
      notes: ''
    });
  }

  async function handleSubmitForm(e) {
    e.preventDefault();

    try {
      setError(null);
      setSuccessMessage('');

      await grantAdminAccess(formData);
      setSuccessMessage('Admin access granted successfully!');

      setShowForm(false);
      setFormData({
        onshapeUserId: '',
        userEmail: '',
        userDisplayName: '',
        role: 'admin',
        notes: ''
      });

      loadAdminUsers();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error granting admin access:', err);
      setError(err.message);
    }
  }

  async function handleRevoke(id, userId) {
    // Prevent self-revocation
    if (adminUser && adminUser.id === userId) {
      alert('You cannot revoke your own admin access.');
      return;
    }

    if (!window.confirm('Are you sure you want to revoke admin access for this user?')) {
      return;
    }

    try {
      setError(null);
      await revokeAdminAccess(id);
      setSuccessMessage('Admin access revoked successfully!');
      loadAdminUsers();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error revoking admin access:', err);
      setError(err.message);
    }
  }

  if (loading) {
    return <div className="admin-loading">Loading admin users...</div>;
  }

  return (
    <div className="admin-user-management">
      <div className="management-header">
        <h2>Admin User Management</h2>
        <div className="management-actions">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            Show Inactive
          </label>
          <button onClick={() => setShowForm(true)} className="btn btn-primary">
            Grant Admin Access
          </button>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {successMessage && <div className="alert alert-success">{successMessage}</div>}

      {showForm && (
        <div className="form-modal">
          <div className="form-modal-content">
            <div className="form-header">
              <h3>Grant Admin Access</h3>
            </div>
            <form onSubmit={handleSubmitForm} className="admin-form">
              <div className="form-group">
                <label htmlFor="onshapeUserId">
                  Onshape User ID <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="onshapeUserId"
                  name="onshapeUserId"
                  value={formData.onshapeUserId}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., 5f3c4a8b9e7d6c5a4b3d2e1f"
                />
                <small className="help-text">
                  The Onshape user ID from their session/profile
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="userEmail">
                  Email <span className="required">*</span>
                </label>
                <input
                  type="email"
                  id="userEmail"
                  name="userEmail"
                  value={formData.userEmail}
                  onChange={handleInputChange}
                  required
                  placeholder="user@example.com"
                />
              </div>

              <div className="form-group">
                <label htmlFor="userDisplayName">Display Name</label>
                <input
                  type="text"
                  id="userDisplayName"
                  name="userDisplayName"
                  value={formData.userDisplayName}
                  onChange={handleInputChange}
                  placeholder="John Doe"
                />
              </div>

              <div className="form-group">
                <label htmlFor="role">Role</label>
                <select
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                >
                  <option value="admin">Admin</option>
                  <option value="viewer">Viewer</option>
                </select>
                <small className="help-text">
                  Admin: Full access. Viewer: Read-only access (future)
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="notes">Notes</label>
                <textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Optional notes about this admin user..."
                />
              </div>

              <div className="form-actions">
                <button type="button" onClick={handleCancelForm} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Grant Access
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="admin-user-list">
        {adminUsers.length === 0 ? (
          <div className="empty-state">
            No admin users found. Click "Grant Admin Access" to add one.
          </div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Display Name</th>
                <th>Email</th>
                <th>Onshape User ID</th>
                <th>Role</th>
                <th>Status</th>
                <th>Created</th>
                <th>Created By</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {adminUsers.map((user) => (
                <tr key={user.id} className={!user.is_active ? 'inactive-row' : ''}>
                  <td>{user.id}</td>
                  <td>{user.user_display_name || '-'}</td>
                  <td>{user.user_email}</td>
                  <td><code className="small-code">{user.onshape_user_id}</code></td>
                  <td>
                    <span className="badge badge-secondary">{user.role}</span>
                  </td>
                  <td>
                    <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{new Date(user.created_at).toLocaleString()}</td>
                  <td>{user.created_by || '-'}</td>
                  <td>
                    <div className="action-buttons">
                      {user.is_active && (
                        <button
                          onClick={() => handleRevoke(user.id, user.onshape_user_id)}
                          className="btn btn-small btn-danger"
                          disabled={adminUser && adminUser.id === user.onshape_user_id}
                          title={
                            adminUser && adminUser.id === user.onshape_user_id
                              ? 'Cannot revoke your own access'
                              : 'Revoke admin access'
                          }
                        >
                          Revoke
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AdminUserManagement;
